import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Typography,
  Stack,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MoneyIcon,
  Schedule as ScheduleIcon,
  Lightbulb as LightbulbIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { User } from '@garbera/shared';

export const SalesProfilePromptBanner: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // 営業実績情報の入力状況をチェック
  const checkSalesProfileCompletion = (user: User | null) => {
    if (!user) return { isComplete: true, missingFields: [] };

    const salesFields = [
      { key: 'mediationCount', label: '仲介実績', icon: TrendingUpIcon },
      { key: 'monthlyMaxSales', label: '月間最高売り上げ', icon: MoneyIcon },
      { key: 'totalSales', label: '総売り上げ', icon: MoneyIcon },
      { key: 'realEstateExperienceMonths', label: '不動産歴', icon: ScheduleIcon },
      { key: 'entrepreneurialInterest', label: '起業志向', icon: LightbulbIcon },
    ];

    const missingFields = salesFields.filter(field => {
      const value = user[field.key as keyof User];
      return value === undefined || value === null;
    });

    return {
      isComplete: missingFields.length === 0,
      missingFields,
      completedCount: salesFields.length - missingFields.length,
      totalCount: salesFields.length,
    };
  };

  const salesProfileStatus = checkSalesProfileCompletion(user);

  const handleNavigateToProfile = () => {
    navigate('/mypage');
  };

  // 営業実績が完成している場合は表示しない
  if (salesProfileStatus.isComplete || !user) {
    return null;
  }

  return (
    <Alert
      severity="info"
      sx={{
        mx: 2,
        mt: 3,
        mb: 2,
        borderRadius: 2,
        backgroundColor: 'primary.50',
        borderColor: 'primary.200',
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
      icon={<PersonIcon />}
    >
      <AlertTitle sx={{ mb: 1, fontWeight: 'bold' }}>
        🏠 プロフィールをより充実させませんか？
      </AlertTitle>
      
      <Typography variant="body2" sx={{ mb: 2 }}>
        営業実績・経験情報を入力すると、より適切なサポートが受けられます。
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          入力状況: {salesProfileStatus.completedCount}/{salesProfileStatus.totalCount} 項目完了
        </Typography>
        
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {salesProfileStatus.missingFields.slice(0, 3).map((field) => {
            const IconComponent = field.icon;
            return (
              <Chip
                key={field.key}
                label={field.label}
                size="small"
                variant="outlined"
                color="warning"
                icon={<IconComponent />}
              />
            );
          })}
          {salesProfileStatus.missingFields.length > 3 && (
            <Chip
              label={`+${salesProfileStatus.missingFields.length - 3}項目`}
              size="small"
              variant="outlined"
              color="default"
            />
          )}
        </Stack>
      </Box>

      <Button
        variant="contained"
        size="small"
        onClick={handleNavigateToProfile}
        endIcon={<ArrowForwardIcon />}
        sx={{
          backgroundColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
        }}
      >
        プロフィールを入力
      </Button>
    </Alert>
  );
}; 